import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  ChevronLeft, 
  Euro,
  Plus,
  Minus,
  CalendarPlus,
  User,
  Mail,
  Phone
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AGE_LABELS = {
  'enfants': 'Enfants',
  'ados': 'Ados',
  'adultes': 'Adultes',
  'tous': 'Tout public'
};

const TYPE_LABELS = {
  'jeu': 'Jeu de société',
  'lecture': 'Lecture',
  'tcg': 'TCG',
  'atelier': 'Atelier',
  'autre': 'Événement'
};

export default function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    num_participants: 1
  });

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/events/${eventId}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Événement non trouvé');
        }
        throw new Error('Erreur lors du chargement');
      }
      const data = await response.json();
      setEvent(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleParticipantsChange = (delta) => {
    const newValue = Math.max(1, Math.min(10, formData.num_participants + delta));
    // Check if there are enough places
    const placesRestantes = event.max_participants - event.current_participants;
    if (newValue <= placesRestantes) {
      setFormData(prev => ({ ...prev, num_participants: newValue }));
    } else {
      toast.error(`Il ne reste que ${placesRestantes} place(s) disponible(s)`);
    }
  };

  const handleSubmit = async (e, joinWaitlist = false) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSubmitting(true);
      const url = joinWaitlist 
        ? `${BACKEND_URL}/api/events/${eventId}/register?waitlist=true`
        : `${BACKEND_URL}/api/events/${eventId}/register`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de l\'inscription');
      }

      const result = await response.json();
      
      // Navigate to confirmation page
      navigate('/confirmation', { 
        state: { 
          registration: result,
          event: event,
          isWaitlist: result.is_waitlist || joinWaitlist
        }
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const addToCalendar = () => {
    if (!event) return;
    
    const startDate = new Date(event.date);
    const endDate = event.end_time ? new Date(event.end_time) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.name)}&dates=${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}Z&details=${encodeURIComponent(event.description || '')}&location=${encodeURIComponent(event.location)}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF7]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 skeleton rounded" />
            <div className="h-64 skeleton rounded-2xl" />
            <div className="space-y-3">
              <div className="h-6 w-3/4 skeleton rounded" />
              <div className="h-4 w-1/2 skeleton rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFDF7]">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="font-heading font-bold text-2xl text-[#2D2A26] mb-2">{error}</h2>
          <p className="text-[#645E55] mb-6">L'événement demandé n'existe pas ou a été supprimé.</p>
          <Link to="/">
            <Button className="btn-primary">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Retour aux événements
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const eventDate = new Date(event.date);
  const placesRestantes = event.max_participants - event.current_participants;
  const canRegister = !event.is_full && !event.is_cancelled && placesRestantes > 0;

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {/* Back link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-[#645E55] hover:text-[#2D2A26] mb-6 transition-colors"
          data-testid="back-link"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Retour aux événements
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero image */}
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src={event.image_url || 'https://images.unsplash.com/photo-1758687126183-74fde92c6338?crop=entropy&cs=srgb&fm=jpg&q=85'}
                alt={event.name}
                className="w-full h-64 md:h-80 object-cover"
              />
              
              {/* Status overlay */}
              {event.is_cancelled && (
                <div className="absolute inset-0 bg-red-500/80 flex items-center justify-center">
                  <span className="text-white font-bold text-2xl uppercase tracking-wider">Annulé</span>
                </div>
              )}
              {event.is_full && !event.is_cancelled && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white font-bold text-2xl uppercase tracking-wider">Complet</span>
                </div>
              )}
              
              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                <Badge className="bg-[#48b4ff] text-white">
                  {TYPE_LABELS[event.event_type] || event.event_type}
                </Badge>
                <Badge className="bg-[#E5E0D8] text-[#645E55]">
                  {AGE_LABELS[event.age_group] || event.age_group}
                </Badge>
              </div>
              
              <div className="absolute top-4 right-4">
                {event.is_free ? (
                  <Badge className="bg-green-500 text-white font-bold text-lg px-4 py-2">Gratuit</Badge>
                ) : (
                  <Badge className="bg-[#ffd451] text-[#2D2A26] font-bold text-lg px-4 py-2">{event.price}€</Badge>
                )}
              </div>
            </div>

            {/* Event info */}
            <div className="space-y-6">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2D2A26]" data-testid="event-title">
                {event.name}
              </h1>

              {/* Meta info */}
              <div className="flex flex-wrap gap-6 text-[#645E55]">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#db3b17]" />
                  <span className="font-medium">
                    {format(eventDate, "EEEE d MMMM yyyy", { locale: fr })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#db3b17]" />
                  <span className="font-medium">
                    {format(eventDate, "HH'h'mm", { locale: fr })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#db3b17]" />
                  <span className="font-medium">{event.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#db3b17]" />
                  <span className="font-medium">
                    {placesRestantes > 0 ? `${placesRestantes} place${placesRestantes > 1 ? 's' : ''} restante${placesRestantes > 1 ? 's' : ''}` : 'Complet'}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="prose prose-lg max-w-none">
                <p className="text-[#2D2A26] leading-relaxed whitespace-pre-wrap">
                  {event.description}
                </p>
              </div>

              {/* Add to calendar button */}
              <Button 
                variant="outline"
                onClick={addToCalendar}
                className="border-2 border-[#E5E0D8] hover:bg-[#FFF8E1] rounded-xl"
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Ajouter à mon agenda
              </Button>
            </div>
          </div>

          {/* Registration sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-2 border-[#E5E0D8] rounded-2xl shadow-card">
              <CardHeader className="bg-[#FFF8E1] rounded-t-2xl">
                <CardTitle className="font-heading text-xl text-[#2D2A26]">
                  {canRegister ? 'Inscription' : 'Inscription fermée'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {canRegister ? (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="registration-form">
                    {/* First name */}
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-[#2D2A26] font-medium">
                        Prénom *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
                        <Input
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleInputChange}
                          placeholder="Votre prénom"
                          required
                          className="pl-10 h-12 border-2 border-[#E5E0D8] rounded-xl focus:border-[#48b4ff]"
                          data-testid="input-first-name"
                        />
                      </div>
                    </div>

                    {/* Last name */}
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-[#2D2A26] font-medium">
                        Nom *
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
                        <Input
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleInputChange}
                          placeholder="Votre nom"
                          required
                          className="pl-10 h-12 border-2 border-[#E5E0D8] rounded-xl focus:border-[#48b4ff]"
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#2D2A26] font-medium">
                        Email *
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="votre@email.com"
                          required
                          className="pl-10 h-12 border-2 border-[#E5E0D8] rounded-xl focus:border-[#48b4ff]"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#2D2A26] font-medium">
                        Téléphone <span className="text-[#645E55] font-normal">(recommandé)</span>
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#645E55]" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="06 12 34 56 78"
                          className="pl-10 h-12 border-2 border-[#E5E0D8] rounded-xl focus:border-[#48b4ff]"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    {/* Number of participants */}
                    <div className="space-y-2">
                      <Label className="text-[#2D2A26] font-medium">
                        Nombre de participants
                      </Label>
                      <div className="flex items-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleParticipantsChange(-1)}
                          disabled={formData.num_participants <= 1}
                          className="h-12 w-12 rounded-xl border-2 border-[#E5E0D8]"
                          data-testid="btn-minus-participant"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-2xl font-bold text-[#2D2A26] w-12 text-center" data-testid="participants-count">
                          {formData.num_participants}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => handleParticipantsChange(1)}
                          disabled={formData.num_participants >= placesRestantes}
                          className="h-12 w-12 rounded-xl border-2 border-[#E5E0D8]"
                          data-testid="btn-plus-participant"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-[#645E55]">
                        {placesRestantes} place{placesRestantes > 1 ? 's' : ''} disponible{placesRestantes > 1 ? 's' : ''}
                      </p>
                    </div>

                    {/* Price total */}
                    {!event.is_free && (
                      <div className="bg-[#FFF8E1] rounded-xl p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[#645E55]">Total à régler sur place</span>
                          <span className="text-2xl font-bold text-[#2D2A26]">
                            {(event.price * formData.num_participants).toFixed(2)}€
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Submit button */}
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-14 btn-primary text-lg"
                      data-testid="submit-registration"
                    >
                      {submitting ? (
                        <>
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Inscription en cours...
                        </>
                      ) : (
                        "Je m'inscris"
                      )}
                    </Button>

                    <p className="text-xs text-center text-[#645E55]">
                      En vous inscrivant, vous acceptez de recevoir un email de confirmation.
                    </p>
                  </form>
                ) : event.is_cancelled ? (
                  <div className="text-center py-6">
                    <div className="text-4xl mb-4">❌</div>
                    <p className="text-[#645E55]">Cet événement a été annulé.</p>
                    <Link to="/">
                      <Button className="mt-4 btn-secondary">
                        Voir d'autres événements
                      </Button>
                    </Link>
                  </div>
                ) : (
                  /* Event is full - show waitlist option */
                  <div className="space-y-6">
                    <div className="text-center py-4">
                      <div className="text-4xl mb-4">😔</div>
                      <p className="text-[#2D2A26] font-medium">Cet événement est complet.</p>
                      {event.waitlist_count > 0 && (
                        <p className="text-sm text-[#645E55] mt-1">
                          {event.waitlist_count} personne{event.waitlist_count > 1 ? 's' : ''} en liste d'attente
                        </p>
                      )}
                    </div>

                    <div className="border-t border-[#E5E0D8] pt-6">
                      <h4 className="font-heading text-lg text-[#2D2A26] mb-4 text-center">
                        S'inscrire en liste d'attente
                      </h4>
                      <p className="text-sm text-[#645E55] text-center mb-4">
                        Vous serez automatiquement inscrit si une place se libère.
                      </p>

                      <form onSubmit={(e) => handleSubmit(e, true)} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="wl_first_name" className="text-[#2D2A26] font-medium">
                              Prénom *
                            </Label>
                            <Input
                              id="wl_first_name"
                              name="first_name"
                              value={formData.first_name}
                              onChange={handleInputChange}
                              required
                              className="h-11 border-2 border-[#E5E0D8] rounded-xl"
                              data-testid="wl-input-firstname"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="wl_last_name" className="text-[#2D2A26] font-medium">
                              Nom *
                            </Label>
                            <Input
                              id="wl_last_name"
                              name="last_name"
                              value={formData.last_name}
                              onChange={handleInputChange}
                              required
                              className="h-11 border-2 border-[#E5E0D8] rounded-xl"
                              data-testid="wl-input-lastname"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="wl_email" className="text-[#2D2A26] font-medium">
                            Email *
                          </Label>
                          <Input
                            id="wl_email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            className="h-11 border-2 border-[#E5E0D8] rounded-xl"
                            data-testid="wl-input-email"
                          />
                        </div>

                        <Button
                          type="submit"
                          disabled={submitting}
                          className="w-full h-12 bg-[#48b4ff] hover:bg-[#3da0e8] text-white font-semibold rounded-xl"
                          data-testid="submit-waitlist"
                        >
                          {submitting ? (
                            <>
                              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Inscription...
                            </>
                          ) : (
                            "Rejoindre la liste d'attente"
                          )}
                        </Button>
                      </form>
                    </div>

                    <div className="text-center pt-2">
                      <Link to="/">
                        <Button variant="ghost" className="text-[#645E55]">
                          Voir d'autres événements
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
